import React from "react";
import {
  Alert,
  AlertTitle,
  IconButton,
  Snackbar,
  Typography,
  AlertColor,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useStyles } from "../../assets/styles/Theme.tsx";

// Types and Interfaces
interface SystemAlertProps {
  open: boolean;
  type: "success" | "error" | "warning" | "info";
  message: string;
  onClose: () => void;
}

interface CustomTheme {
  palette: {
    system: {
      success: string;
      successTertiary: string;
      error: string;
      errorTertiary: string;
      pending: string;
      pendingTertiary: string;
      submit: string;
      submitTertiary: string;
    };
  };
}

const SystemAlert: React.FC<SystemAlertProps> = ({ open, type, message, onClose }) => {
  const theme = useTheme() as CustomTheme;
  const styles = useStyles(theme);

  const getIcon = (): React.ReactElement => {
    switch (type) {
      case "success":
        return <CheckCircleIcon sx={{ color: theme.palette.system.success }} />;
      case "error":
        return <InfoOutlinedIcon sx={{ color: theme.palette.system.error }} />;
      case "warning":
        return (
          <WarningAmberOutlinedIcon
            sx={{ color: theme.palette.system.pending }}
          />
        );
      case "info":
        return <InfoOutlinedIcon sx={{ color: theme.palette.system.submit }} />;
      default:
        return <CheckCircleIcon />;
    }
  };

  const getAlertStyles = (): React.CSSProperties => {
    switch (type) {
      case "success":
        return {
          backgroundColor: theme.palette.system.successTertiary,
          border: `1px solid ${theme.palette.system.success}`,
          color: theme.palette.system.success,
        };
      case "error":
        return {
          backgroundColor: theme.palette.system.errorTertiary,
          border: `1px solid ${theme.palette.system.error}`,
          color: theme.palette.system.error,
        };
      case "warning":
        return {
          backgroundColor: theme.palette.system.pendingTertiary,
          border: `1px solid ${theme.palette.system.pending}`,
          color: theme.palette.system.pending,
        };
      case "info":
        return {
          backgroundColor: theme.palette.system.submitTertiary,
          border: `1px solid ${theme.palette.system.submit}`,
          color: theme.palette.system.submit,
        };
      default:
        return {};
    }
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      autoHideDuration={2000}
      onClose={onClose}
    >
      <Alert
        severity={type as AlertColor}
        icon={getIcon()}
        sx={{
          ...getAlertStyles(),
          fontWeight: 500,
          boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
          minWidth: 300,
          display: "flex",
          alignItems: "center",
        }}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={onClose}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        }
      >
        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
          {message}
        </Typography>
      </Alert>
    </Snackbar>
  );
};

export default SystemAlert;